// src/pages/user/Profile.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { userAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import Loading from '../../components/common/Loading';

const phoneIsValid = (phone) => {
  if (!phone) return true; // optional
  // accept formats like 06XXXXXXXX or +2126XXXXXXXX
  return /^(\+212|0)?6\d{8}$/.test(phone);
};

const Profile = () => {
  const { user, setUser } = useAuth() || {}; // try to use setUser if provided by context
  const mountedRef = useRef(true);

  const [loading, setLoading] = useState(false);       // saving state
  const [initialLoading, setInitialLoading] = useState(true); // initial load state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });

  // Fill form from context user if available, otherwise fetch latest profile
  const loadProfileFromContext = useCallback(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || ''
      });
      setInitialLoading(false);
      return true;
    }
    return false;
  }, [user]);

  const fetchProfile = useCallback(async () => {
    setInitialLoading(true);
    try {
      const res = await userAPI.getProfile?.(); // if available
      const payload = res?.data ?? res ?? {};
      const profile = payload?.data ?? payload ?? {};
      if (!mountedRef.current) return;
      setFormData({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        email: profile.email || '',
        phone: profile.phone || ''
      });
    } catch (err) {
      console.error('Erreur chargement profil:', err);
      toast.warn('Impossible de récupérer les informations de profil (utilisation des données du contexte si disponibles).');
      loadProfileFromContext();
    } finally {
      if (mountedRef.current) setInitialLoading(false);
    }
  }, [loadProfileFromContext]);

  useEffect(() => {
    // prefer context user, otherwise fetch from API
    const usedContext = loadProfileFromContext();
    if (!usedContext) {
      fetchProfile();
    } else {
      setInitialLoading(false);
    }

    return () => {
      mountedRef.current = false;
    };
  }, [fetchProfile, loadProfileFromContext]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // client-side validation
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      toast.error('Prénom et nom sont requis');
      return;
    }

    if (!phoneIsValid(formData.phone)) {
      toast.error('Format de téléphone incorrect (ex: 06XXXXXXXX ou +2126XXXXXXXX)');
      return;
    }

    setLoading(true);
    try {
      const res = await userAPI.updateProfile(formData);
      // Accept multiple shapes: res.data.data | res.data | res
      const payload = res?.data?.data ?? res?.data ?? res ?? {};
      // update local UI
      if (mountedRef.current) {
        // try to sync auth context if setUser exists
        if (typeof setUser === 'function') {
          const updatedUser = {
            ...(user || {}),
            ...payload,
            firstName: payload.firstName ?? formData.firstName,
            lastName: payload.lastName ?? formData.lastName,
            phone: payload.phone ?? formData.phone
          };
          try { setUser(updatedUser); } catch (err) { /* ignore if not supported */ }
        }
        setFormData(prev => ({
          ...prev,
          firstName: payload.firstName ?? prev.firstName,
          lastName: payload.lastName ?? prev.lastName,
          phone: payload.phone ?? prev.phone
        }));
      }

      toast.success('Profil mis à jour avec succès');
    } catch (error) {
      console.error('Erreur updateProfile:', error);
      // try to show backend message
      const msg = error?.response?.data?.message || error?.message || 'Erreur lors de la mise à jour';
      toast.error(msg);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  if (initialLoading) return <Loading />;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-2xl font-bold mb-6">Informations personnelles</h2>

      <form onSubmit={handleSubmit}>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-gray-700 font-medium mb-2">Prénom</label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              autoComplete="given-name"
            />
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-2">Nom</label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              autoComplete="family-name"
            />
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-gray-700 font-medium mb-2">Email</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            disabled
            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
          />
          <p className="text-sm text-gray-500 mt-1">L'email ne peut pas être modifié</p>
        </div>

        <div className="mb-6">
          <label className="block text-gray-700 font-medium mb-2">Téléphone</label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="06XXXXXXXX"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            autoComplete="tel"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-50 flex items-center"
        >
          {loading ? (
            <svg className="animate-spin h-5 w-5 mr-3 text-white" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16 8 8 0 01-8-8z" />
            </svg>
          ) : null}
          {loading ? 'Mise à jour...' : 'Enregistrer les modifications'}
        </button>
      </form>
    </div>
  );
};

export default Profile;
